import { ASSET_MAX_BYTES } from '@shared/media/uploadPolicy';
import { MediaProcessingError } from './errors';
import type { MediaProcessingMetadata, MediaProcessingUse, MediaQualityIntent } from './types';

/** The encoder is injected so every caller must explicitly provide client-side processing. */
export const VIDEO_PROCESSING_POLICY_VERSION = 'media-client-v1';

export type VideoProcessingBudget = {
  maxInputBytes: number;
  maxDurationMs: number;
  maxPixels: number;
  maxOutputBytes: number;
};

export type VideoOutputContract = {
  allowedMimeTypes: readonly ['video/mp4', 'video/webm'] | readonly string[];
  maxOutputBytes: number;
};

export type VideoInspection = MediaProcessingMetadata & {
  mimeType: string;
  fileName: string;
  sizeBytes: number;
  width: number;
  height: number;
  durationMs: number;
};

export type VideoCapabilityReport = {
  browserVideoElement: boolean;
  worker: boolean;
  decoderMimeTypes: Record<string, boolean>;
  videoEncoderApi: boolean;
  ffmpegWorkerPrerequisites: boolean;
  reasons: string[];
};

export type ProcessedVideo = {
  file: File;
  metadata: MediaProcessingMetadata;
  source: VideoInspection;
  policyVersion: string;
};

export type VideoTranscoder = (input: {
  file: File;
  source: VideoInspection;
  quality: MediaQualityIntent;
  use: MediaProcessingUse;
  signal?: AbortSignal;
}) => Promise<{ file: File; metadata?: Partial<MediaProcessingMetadata> }>;

function throwIfAborted(signal?: AbortSignal) {
  if (signal?.aborted) throw new MediaProcessingError('cancelled', '视频处理已取消');
}

function assertBudget(budget: VideoProcessingBudget) {
  for (const [name, value] of Object.entries(budget)) {
    if (!Number.isFinite(value) || value <= 0) throw new Error(`视频处理预算 ${name} 必须为正数`);
  }
}

function sourceMetadata(file: File): MediaProcessingMetadata {
  return { mimeType: file.type || 'application/octet-stream', fileName: file.name, sizeBytes: file.size };
}

/** Read container metadata without decoding or uploading the source payload. */
export async function inspectVideo(file: File, budget: VideoProcessingBudget, signal?: AbortSignal): Promise<VideoInspection> {
  assertBudget(budget);
  throwIfAborted(signal);
  if (!file.type.startsWith('video/')) throw new MediaProcessingError('unsupported-input', '请选择视频文件');
  if (file.size <= 0) throw new MediaProcessingError('unsupported-input', '视频文件为空');
  if (file.size > budget.maxInputBytes) throw new MediaProcessingError('resource-exhausted', '视频文件超过客户端输入预算');
  if (typeof document === 'undefined' || typeof URL === 'undefined') {
    throw new MediaProcessingError('unsupported-input', '当前环境不支持视频元数据检查');
  }

  const element = document.createElement('video');
  const objectUrl = URL.createObjectURL(file);
  try {
    element.preload = 'metadata';
    element.src = objectUrl;
    await new Promise<void>((resolve, reject) => {
      const onLoaded = () => { cleanup(); resolve(); };
      const onError = () => { cleanup(); reject(new MediaProcessingError('unsupported-input', '视频容器无法读取')); };
      const onAbort = () => { cleanup(); reject(new MediaProcessingError('cancelled', '视频处理已取消')); };
      const cleanup = () => {
        element.removeEventListener('loadedmetadata', onLoaded);
        element.removeEventListener('error', onError);
        signal?.removeEventListener('abort', onAbort);
      };
      element.addEventListener('loadedmetadata', onLoaded, { once: true });
      element.addEventListener('error', onError, { once: true });
      signal?.addEventListener('abort', onAbort, { once: true });
      throwIfAborted(signal);
    });
    throwIfAborted(signal);
    const width = element.videoWidth;
    const height = element.videoHeight;
    const durationMs = Math.round(element.duration * 1000);
    if (!width || !height || width * height > budget.maxPixels) {
      throw new MediaProcessingError('resource-exhausted', '视频像素尺寸超过客户端处理预算');
    }
    if (!Number.isFinite(element.duration) || durationMs <= 0 || durationMs > budget.maxDurationMs) {
      throw new MediaProcessingError('resource-exhausted', '视频时长超过客户端处理预算');
    }
    return { ...sourceMetadata(file), width, height, durationMs };
  } finally {
    URL.revokeObjectURL(objectUrl);
    element.removeAttribute('src');
    element.load();
  }
}

/**
 * Reports browser primitives only; it does not load FFmpeg or claim that a
 * codec can encode successfully. Actual encoding remains a separate spike.
 */
export async function detectVideoProcessingCapabilities(
  outputMimeTypes: readonly string[] = ['video/mp4', 'video/webm'],
): Promise<VideoCapabilityReport> {
  const reasons: string[] = [];
  const browserVideoElement = typeof document !== 'undefined';
  const worker = typeof Worker !== 'undefined';
  const decoderMimeTypes: Record<string, boolean> = {};
  if (browserVideoElement) {
    const element = document.createElement('video');
    for (const mimeType of outputMimeTypes) decoderMimeTypes[mimeType] = element.canPlayType(mimeType) !== '';
  } else reasons.push('document/video element unavailable');
  if (!worker) reasons.push('Worker unavailable');
  const videoEncoderApi = 'VideoEncoder' in globalThis;
  if (!videoEncoderApi) reasons.push('WebCodecs VideoEncoder unavailable');
  return {
    browserVideoElement,
    worker,
    decoderMimeTypes,
    videoEncoderApi,
    ffmpegWorkerPrerequisites: browserVideoElement && worker,
    reasons,
  };
}

export function validateVideoOutput(file: File, metadata: MediaProcessingMetadata, contract: VideoOutputContract) {
  if (!contract.allowedMimeTypes.includes(file.type as never)) {
    throw new MediaProcessingError('output-invalid', `视频输出格式不在允许集合: ${file.type || 'unknown'}`);
  }
  if (file.size <= 0 || file.size > contract.maxOutputBytes || file.size > ASSET_MAX_BYTES) {
    throw new MediaProcessingError('output-invalid', '视频输出大小不符合契约');
  }
  if (metadata.mimeType !== file.type || metadata.sizeBytes !== file.size) {
    throw new MediaProcessingError('output-invalid', '视频输出元数据与文件不一致');
  }
}

/** Processing entry point. It never falls back to uploading the source file. */
export async function runVideoFeasibilitySpike(input: {
  file: File;
  budget: VideoProcessingBudget;
  contract: VideoOutputContract;
  use: MediaProcessingUse;
  quality: MediaQualityIntent;
  transcode?: VideoTranscoder;
  signal?: AbortSignal;
}): Promise<ProcessedVideo> {
  const source = await inspectVideo(input.file, input.budget, input.signal);
  if (!input.transcode) throw new MediaProcessingError('unsupported-input', '视频编码器不可用，无法处理且不会上传原视频');
  throwIfAborted(input.signal);
  const result = await input.transcode({ file: input.file, source, quality: input.quality, use: input.use, signal: input.signal });
  throwIfAborted(input.signal);
  if (result.file === input.file) throw new MediaProcessingError('output-invalid', '视频处理禁止原文件作为输出');
  const metadata: MediaProcessingMetadata = {
    ...source,
    ...result.metadata,
    mimeType: result.file.type,
    fileName: result.file.name,
    sizeBytes: result.file.size,
  };
  validateVideoOutput(result.file, metadata, input.contract);
  return { file: result.file, metadata, source, policyVersion: VIDEO_PROCESSING_POLICY_VERSION };
}
